import React, { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { useToast } from '../ui/ToastProvider'
import { formatMenuInstant } from '../lib/date'

const DEFAULT_CREATE_FORM = {
  userId: '',
  webEnabled: true,
  appEnabled: true,
  kakaoEnabled: false,
  active: true,
}

export default function OperatorAlertManagementPage() {
  const { show } = useToast()
  const [activeTab, setActiveTab] = useState('targets')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [targets, setTargets] = useState([])
  const [candidateQuery, setCandidateQuery] = useState('')
  const [candidates, setCandidates] = useState([])
  const [createForm, setCreateForm] = useState(DEFAULT_CREATE_FORM)

  const loadAll = async (query = candidateQuery) => {
    setLoading(true)
    setError(null)
    try {
      const [targetsRes, candidatesRes] = await Promise.all([
        api.get('/operator-alert-targets'),
        api.get('/operator-alert-targets/candidates', { params: query ? { q: query } : {} }),
      ])
      const targetPayload = targetsRes?.data?.data ?? targetsRes?.data ?? []
      const candidatePayload = candidatesRes?.data?.data ?? candidatesRes?.data ?? []
      setTargets(Array.isArray(targetPayload) ? targetPayload.map((item) => ({ ...item })) : [])
      setCandidates(Array.isArray(candidatePayload) ? candidatePayload : [])
    } catch (e) {
      setError('운영자 알림 대상을 불러오지 못했습니다.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadAll('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      void loadAll(candidateQuery)
    }, 250)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateQuery])

  const availableCandidates = useMemo(
    () => candidates.filter((item) => !item.configured),
    [candidates],
  )

  const onCreate = async () => {
    if (!createForm.userId) {
      show('추가할 운영자를 선택해주세요.', 'error')
      return
    }
    setSaving(true)
    try {
      await api.post('/operator-alert-targets', {
        userId: Number(createForm.userId),
        webEnabled: Boolean(createForm.webEnabled),
        appEnabled: Boolean(createForm.appEnabled),
        kakaoEnabled: Boolean(createForm.kakaoEnabled),
        active: Boolean(createForm.active),
      })
      show('운영자 알림 대상을 추가했습니다.', 'success')
      setCreateForm(DEFAULT_CREATE_FORM)
      await loadAll(candidateQuery)
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '추가에 실패했습니다.'
      show(msg, 'error')
    } finally {
      setSaving(false)
    }
  }

  const updateTargetField = (id, field, value) => {
    setTargets((prev) => prev.map((item) => (
      item.id === id
        ? { ...item, [field]: value, __dirty: true }
        : item
    )))
  }

  const saveTarget = async (target) => {
    setSaving(true)
    try {
      const res = await api.patch(`/operator-alert-targets/${target.id}`, {
        webEnabled: Boolean(target.webEnabled),
        appEnabled: Boolean(target.appEnabled),
        kakaoEnabled: Boolean(target.kakaoEnabled),
        active: Boolean(target.active),
      })
      const updated = res?.data?.data ?? res?.data ?? target
      setTargets((prev) => prev.map((item) => (
        item.id === target.id ? { ...updated, __dirty: false } : item
      )))
      show('저장되었습니다.', 'success')
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '저장에 실패했습니다.'
      show(msg, 'error')
    } finally {
      setSaving(false)
    }
  }

  const deleteTarget = async (target) => {
    if (!window.confirm(`"${target.name || target.email}" 운영자를 알림 대상에서 제거할까요?`)) return
    setSaving(true)
    try {
      await api.delete(`/operator-alert-targets/${target.id}`)
      show('삭제되었습니다.', 'success')
      await loadAll(candidateQuery)
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '삭제에 실패했습니다.'
      show(msg, 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">알림관리</h1>
      </div>

      <div className="rounded border bg-white">
        <div className="border-b px-4 pt-4">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={`rounded-t-lg border px-4 py-2 text-sm ${activeTab === 'targets' ? 'border-b-white bg-white font-semibold text-brand-navy' : 'border-transparent bg-slate-50 text-slate-600'}`}
              onClick={() => setActiveTab('targets')}
            >
              운영자 알림 대상
            </button>
          </div>
        </div>

        {activeTab === 'targets' && (
          <div className="space-y-6 p-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              설정된 활성 대상이 하나도 없으면 기존 동작과 동일하게 전체 `OPERATOR` 사용자에게 WEB/APP 알림이 fallback 됩니다.
              <div className="mt-1 text-xs text-amber-800">
                `KAKAO` 채널은 현재 대상 관리만 선반영되며, 실제 운영 템플릿 연동은 후속 작업으로 연결됩니다.
              </div>
            </div>

            <div className="rounded border bg-slate-50 p-4 space-y-4">
              <div>
                <div className="font-semibold">알림 대상 운영자 추가</div>
                <div className="mt-1 text-xs text-slate-500">
                  OPERATOR 권한 사용자만 등록할 수 있습니다.
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,2fr)_auto]">
                <div className="space-y-3">
                  <input
                    className="w-full rounded border px-3 py-2 text-sm"
                    placeholder="운영자 검색 (이름/이메일/조직)"
                    value={candidateQuery}
                    onChange={(e) => setCandidateQuery(e.target.value)}
                  />
                  <select
                    className="w-full rounded border px-3 py-2 text-sm"
                    value={createForm.userId}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, userId: e.target.value }))}
                  >
                    <option value="">추가할 운영자를 선택하세요</option>
                    {availableCandidates.map((candidate) => (
                      <option key={candidate.userId} value={String(candidate.userId)}>
                        {candidate.name || '-'} / {candidate.email} / {candidate.orgName || '-'}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    type="button"
                    className="rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                    onClick={onCreate}
                    disabled={saving || !createForm.userId}
                  >
                    추가
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap gap-4 text-sm">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={createForm.active}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, active: e.target.checked }))}
                  />
                  활성
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={createForm.webEnabled}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, webEnabled: e.target.checked }))}
                  />
                  WEB
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={createForm.appEnabled}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, appEnabled: e.target.checked }))}
                  />
                  APP
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={createForm.kakaoEnabled}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, kakaoEnabled: e.target.checked }))}
                  />
                  KAKAO
                </label>
              </div>
            </div>

            <div className="rounded border">
              <div className="border-b bg-slate-50 px-4 py-3">
                <div className="font-semibold">등록된 운영자 알림 대상</div>
              </div>

              {loading ? (
                <div className="px-4 py-8 text-sm text-slate-500">불러오는 중...</div>
              ) : error ? (
                <div className="space-y-3 px-4 py-8 text-sm text-red-600">
                  <div>{error}</div>
                  <button
                    type="button"
                    className="rounded border px-3 py-1.5 text-sm text-slate-700"
                    onClick={() => loadAll(candidateQuery)}
                  >
                    다시 시도
                  </button>
                </div>
              ) : targets.length === 0 ? (
                <div className="px-4 py-8 text-sm text-slate-500">
                  아직 등록된 운영자 알림 대상이 없습니다.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-4 py-2 text-left">운영자</th>
                        <th className="px-4 py-2 text-left">조직</th>
                        <th className="px-4 py-2 text-left">상태</th>
                        <th className="px-4 py-2 text-left">채널</th>
                        <th className="px-4 py-2 text-left">수정일</th>
                        <th className="px-4 py-2 text-right">관리</th>
                      </tr>
                    </thead>
                    <tbody>
                      {targets.map((target) => (
                        <tr key={target.id} className="border-t">
                          <td className="px-4 py-3">
                            <div className="font-medium text-slate-900">{target.name || '-'}</div>
                            <div className="text-xs text-slate-500">{target.email || '-'}</div>
                          </td>
                          <td className="px-4 py-3 text-slate-600">{target.orgName || '-'}</td>
                          <td className="px-4 py-3">
                            <label className="inline-flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={Boolean(target.active)}
                                onChange={(e) => updateTargetField(target.id, 'active', e.target.checked)}
                              />
                              <span className={target.active ? 'text-green-700' : 'text-slate-500'}>
                                {target.active ? '활성' : '비활성'}
                              </span>
                            </label>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-3">
                              <label className="inline-flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={Boolean(target.webEnabled)}
                                  onChange={(e) => updateTargetField(target.id, 'webEnabled', e.target.checked)}
                                />
                                WEB
                              </label>
                              <label className="inline-flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={Boolean(target.appEnabled)}
                                  onChange={(e) => updateTargetField(target.id, 'appEnabled', e.target.checked)}
                                />
                                APP
                              </label>
                              <label className="inline-flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={Boolean(target.kakaoEnabled)}
                                  onChange={(e) => updateTargetField(target.id, 'kakaoEnabled', e.target.checked)}
                                />
                                KAKAO
                              </label>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">
                            {formatMenuInstant(target, 'updatedAt')}
                            <div>{target.updatedBy || '-'}</div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                className="rounded border px-3 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-60"
                                disabled={!target.__dirty || saving}
                                onClick={() => saveTarget(target)}
                              >
                                저장
                              </button>
                              <button
                                type="button"
                                className="rounded border border-red-200 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 disabled:opacity-60"
                                disabled={saving}
                                onClick={() => deleteTarget(target)}
                              >
                                삭제
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
